import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { QrService } from '../../services/qr-service.service';
import { CartService } from '../../services/cart.service';
import { ReservationService } from '../../services/reservation.service';
import { AuthService } from '../../services/auth.service';
import { BusinessService, BusinessDetailApi } from '../../services/business.service';
import { FeedbackService, CreateFeedbackRequest } from '../../services/feedback.service';
import { ConfigService } from '../../services/config.service';
import { TimeSlot, ReservationRequest } from '../../models/reservation.models';

interface BusinessDetail {
  id: string;
  slug?: string;
  name: string;
  address: string;
  phone: string;
  cuisineType: string;
  priceRange: string;
  rating: number;
  reviewCount: number;
  isOpen: boolean;
  coverImage?: string;
  logoImage?: string;
  description: string;
  specialOffers: string[];
  hours: string;
  menus?: any[];
  // Capabilities from API
  allows_dine_in: boolean;
  allows_takeaway: boolean;
  allows_delivery: boolean;
  reservation_enabled: boolean;
  business_type?: string;
  booking_mode?: string; // 'none' | 'table' | 'service' | 'appointment'
}

interface ServiceChip {
  id: string;
  label: string;
  icon: string;
  action: () => void;
}

@Component({
  selector: 'app-business-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './business-detail.component.html',
  styleUrl: './business-detail.component.scss'
})
export class BusinessDetailComponent implements OnInit {
  business: BusinessDetail | null = null;
  loading = true;
  loadingMenu = false;
  menuError: string | null = null;
  serviceChips: ServiceChip[] = [];

  // Cached menu data (from menu_cache or NATS)
  private cachedMenuData: any = null;
  menuAvailable = false;
  menuCategories: any[] = [];
  menuProducts: any[] = [];

  // Order mode modal state
  showOrderModal = false;
  orderModalMode: 'takeaway' | 'delivery' = 'takeaway';
  orderCustomerName = '';
  orderCustomerPhone = '';
  orderCustomerAddress = '';
  orderUseProfileAddress = true;
  profileAddress = '';

  // Feedback modal state
  showFeedbackModal = false;
  feedbackType: 'suggestion' | 'complaint' | 'review' = 'suggestion';
  feedbackTitle = '';
  feedbackContent = '';
  feedbackRating = 0;
  feedbackCategoryFood = 0;
  feedbackCategoryService = 0;
  feedbackCategoryAmbience = 0;
  feedbackCategoryValue = 0;
  submittingFeedback = false;
  feedbackSuccess = false;
  feedbackError = '';

  // Reservation modal state
  showReservationModal = false;
  reservationStep: 'service' | 'date' | 'time' | 'details' | 'confirm' | 'success' = 'date';
  reservationDate = '';
  minDate = '';
  timeSlots: TimeSlot[] = [];
  selectedSlot: TimeSlot | null = null;
  loadingSlots = false;
  partySize = 2;
  reservationNotes = '';
  customerName = '';
  customerPhone = '';
  submittingReservation = false;
  reservationError = '';

  // Services/Professionals/Resources (loaded from TPVSecure)
  services: { id: number; name: string; duration_minutes?: number; price?: number }[] = [];
  professionals: { id: number; name: string; specialties?: string[] }[] = [];
  resources: { id: number; name: string; type: string; capacity?: number }[] = [];
  selectedServiceId: number | null = null;
  selectedProfessionalId: number | null = null;
  loadingServices = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private qrService: QrService,
    private cartService: CartService,
    private reservationService: ReservationService,
    private authService: AuthService,
    private businessService: BusinessService,
    private feedbackService: FeedbackService,
    private configService: ConfigService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    const state = history.state;

    if (id) {
      // Try to fetch from API first
      this.businessService.getBusinessDetail(id).subscribe({
        next: (apiData) => {
          this.business = this.mapApiToBusiness(apiData, state?.['businessData']);
          this.buildServiceChips();
          this.loading = false;
          this.cdr.detectChanges();
          // Load menu from cache if slug is available
          if (this.business.slug) {
            this.loadMenuCache(this.business.slug);
          }
        },
        error: () => {
          // Fallback to router state if API fails
          if (state && state['businessData']) {
            this.business = {
              ...state['businessData'],
              allows_dine_in: false,
              allows_takeaway: false,
              allows_delivery: false,
              reservation_enabled: false,
            };
          } else {
            this.business = {
              id,
              name: 'Negocio no encontrado',
              address: '',
              phone: '',
              cuisineType: '',
              priceRange: '',
              rating: 0,
              reviewCount: 0,
              isOpen: false,
              description: '',
              specialOffers: [],
              hours: '',
              allows_dine_in: false,
              allows_takeaway: false,
              allows_delivery: false,
              reservation_enabled: false,
            };
          }
          this.buildServiceChips();
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
    }
  }

  private mapApiToBusiness(api: BusinessDetailApi, stateData?: any): BusinessDetail {
    return {
      id: api.id,
      slug: api.slug,
      name: api.name,
      address: api.address || '',
      phone: api.phone || '',
      cuisineType: stateData?.cuisineType || api.tags?.[0] || '',
      priceRange: stateData?.priceRange || '',
      rating: stateData?.rating || 0,
      reviewCount: stateData?.reviewCount || 0,
      isOpen: api.is_open,
      coverImage: api.cover_image_url || stateData?.coverImage,
      logoImage: api.logo_url || stateData?.logoImage,
      description: api.description || api.short_description || '',
      specialOffers: stateData?.specialOffers || [],
      hours: this.formatHours(api.hours),
      menus: stateData?.menus,
      allows_dine_in: api.allows_dine_in,
      allows_takeaway: api.allows_takeaway,
      allows_delivery: api.allows_delivery,
      reservation_enabled: api.reservation_enabled,
      business_type: api.business_type,
      booking_mode: api.booking_mode,
    };
  }

  private formatHours(hours?: any): string {
    if (!hours) return '';
    const days = [
      { name: 'Lun', data: hours.monday },
      { name: 'Mar', data: hours.tuesday },
      { name: 'Mie', data: hours.wednesday },
      { name: 'Jue', data: hours.thursday },
      { name: 'Vie', data: hours.friday },
      { name: 'Sab', data: hours.saturday },
      { name: 'Dom', data: hours.sunday },
    ];
    return days
      .map(d => {
        if (!d.data || d.data.closed) return `${d.name}: Cerrado`;
        const shift1 = (d.data.open && d.data.close) ? `${d.data.open}-${d.data.close}` : '';
        const shift2 = (d.data.open2 && d.data.close2) ? `${d.data.open2}-${d.data.close2}` : '';
        if (shift1 && shift2) return `${d.name}: ${shift1} / ${shift2}`;
        if (shift1) return `${d.name}: ${shift1}`;
        return `${d.name}: -`;
      })
      .join(' | ');
  }

  private buildServiceChips(): void {
    if (!this.business) return;
    this.serviceChips = [];

    // Dine-in ("Pedir en local") is QR-only from dashboard scan — not shown here

    if (this.business.allows_takeaway) {
      this.serviceChips.push({
        id: 'takeaway',
        label: 'Recoger',
        icon: '🥡',
        action: () => this.onTakeaway()
      });
    }

    if (this.business.allows_delivery) {
      this.serviceChips.push({
        id: 'delivery',
        label: 'Delivery',
        icon: '🛵',
        action: () => this.onDelivery()
      });
    }

    if (this.business.reservation_enabled) {
      this.serviceChips.push({
        id: 'reserve',
        label: 'Reservar',
        icon: '📅',
        action: () => this.onReservar()
      });
    }
  }

  goBack(): void {
    window.history.back();
  }

  onServiceChipClick(chip: ServiceChip): void {
    chip.action();
  }

  onPedir(menu?: any): void {
    if (!this.business) return;

    if (menu?.api_url) {
      this.loadMenuAndNavigate(menu, 'dine-in');
    } else if (this.business.menus && this.business.menus.length === 1) {
      this.loadMenuAndNavigate(this.business.menus[0], 'dine-in');
    } else if (this.cachedMenuData) {
      // Use cached menu from DB
      this.navigateToMenuWithMode('dine-in');
    } else {
      this.menuError = 'La carta de este local aun no esta disponible. Escanea el QR en el establecimiento.';
    }
  }

  onTakeaway(): void {
    if (!this.business) return;
    this.openOrderModal('takeaway');
  }

  onDelivery(): void {
    if (!this.business) return;
    this.openOrderModal('delivery');
  }

  private openOrderModal(mode: 'takeaway' | 'delivery'): void {
    this.orderModalMode = mode;
    this.orderCustomerAddress = '';
    this.profileAddress = '';

    // Pre-fill from user profile
    const user = this.authService.getCurrentUser();
    if (user) {
      this.orderCustomerName = user.name || '';
    }

    // Try cached address first
    const cached = localStorage.getItem('barclik_profile_address');
    if (cached) {
      try {
        const addr = JSON.parse(cached);
        this.profileAddress = addr.street ? `${addr.street}, ${addr.city}` : '';
        this.orderCustomerAddress = this.profileAddress;
        this.orderCustomerPhone = this.orderCustomerPhone || '';
      } catch { /* ignore */ }
    }

    // Fetch fresh profile data (phone + address)
    this.http.get<any>('/api/v1/users/profile').subscribe({
      next: (profile) => {
        this.orderCustomerName = this.orderCustomerName || profile.name || '';
        this.orderCustomerPhone = profile.phone || this.orderCustomerPhone || '';
        if (profile.address?.street) {
          this.profileAddress = `${profile.address.street}, ${profile.address.city || ''}`;
          if (this.orderUseProfileAddress) {
            this.orderCustomerAddress = this.profileAddress;
          }
          localStorage.setItem('barclik_profile_address', JSON.stringify(profile.address));
        }
      }
    });

    this.orderUseProfileAddress = true;
    this.showOrderModal = true;
  }

  closeOrderModal(): void {
    this.showOrderModal = false;
  }

  get isOrderFormValid(): boolean {
    if (!this.orderCustomerName.trim() || !this.orderCustomerPhone.trim()) return false;
    if (this.orderModalMode === 'delivery' && !this.orderCustomerAddress.trim()) return false;
    return true;
  }

  confirmOrderMode(): void {
    if (!this.business || !this.isOrderFormValid) return;

    const mode = this.orderModalMode === 'delivery' ? 'delivery' : 'takeaway';

    // Store customer data for the order flow
    const customerData = {
      service_type: this.orderModalMode,
      customer_name: this.orderCustomerName.trim(),
      customer_phone: this.orderCustomerPhone.trim(),
      customer_address: this.orderCustomerAddress.trim(),
      service_reference: '',
    };
    sessionStorage.setItem('barclik_customer_data', JSON.stringify(customerData));

    this.showOrderModal = false;

    if (this.business.menus && this.business.menus.length === 1) {
      this.loadMenuAndNavigate(this.business.menus[0], mode);
    } else if (this.cachedMenuData) {
      this.navigateToMenuWithMode(mode);
    } else {
      this.menuError = 'La carta de este local aun no esta disponible. Escanea el QR en el establecimiento.';
    }
  }

  onToggleProfileAddress(): void {
    this.orderUseProfileAddress = !this.orderUseProfileAddress;
    if (this.orderUseProfileAddress) {
      this.orderCustomerAddress = this.profileAddress;
    } else {
      this.orderCustomerAddress = '';
    }
  }

  private loadMenuAndNavigate(menu: any, mode: string): void {
    this.loadingMenu = true;
    this.menuError = null;
    this.cdr.detectChanges();

    this.qrService.getMenuByDirectUrl(menu.api_url).subscribe({
      next: (menuData) => {
        this.loadingMenu = false;
        if (menuData?.categories?.length > 0) {
          // Save menu cache for public endpoint (fire and forget)
          if (this.business?.slug) {
            this.http.post(`/api/v1/public/local/${this.business.slug}/cache-menu`, menuData).subscribe({
              next: () => console.log('Menu cache saved for', this.business?.slug),
              error: () => {} // ignore errors
            });
          }

          this.cartService.setOrderMode(mode as any);
          this.router.navigate(['/restaurant-menu'], {
            state: {
              restaurant: {
                id: this.business!.id,
                name: this.business!.name,
                selectedMenu: menu.menu_name
              },
              menu: menuData,
              source: 'business-detail'
            }
          });
        } else {
          this.menuError = 'La carta no tiene productos disponibles.';
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.loadingMenu = false;
        this.menuError = 'Error al cargar la carta. Intenta de nuevo.';
        this.cdr.detectChanges();
      }
    });
  }

  private loadMenuCache(slug: string): void {
    this.http.get<any>(`/api/v1/public/local/${slug}/menu`).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.convertAndStoreCachedMenu(res.data);
          this.menuAvailable = true;
          this.cdr.detectChanges();
        }
      },
      error: () => { /* menu cache not available, that's ok */ }
    });
  }

  private convertAndStoreCachedMenu(externalMenu: any): void {
    const categories = externalMenu.categories || [];
    this.cachedMenuData = {
      id: `menu_${externalMenu.company_id}`,
      restaurant_id: String(externalMenu.company_id),
      name: externalMenu.menu_name || externalMenu.display_title,
      description: externalMenu.menu_description,
      currency: externalMenu.currency || 'EUR',
      language: 'es',
      categories: categories.map((c: any) => ({
        ...c,
        id: String(c.id),
        visible: true,
        items: []
      })),
      products: (externalMenu.products || []).map((p: any) => ({
        ...p,
        id: String(p.id),
        category_id: String(categories.find((c: any) => c.name === p.category)?.id || 0),
        image_url: p.image_url || '',
        has_variants: !!(p.variants && p.variants.length > 0),
      })),
      packs: externalMenu.packs || [],
      updated_at: externalMenu.sync_timestamp,
      version: parseInt(externalMenu.version) || 1
    };

    // Build summary for display
    this.menuCategories = categories.filter((c: any) => c.product_count > 0 || !c.parent_id);
    this.menuProducts = externalMenu.products || [];
  }

  private navigateToMenuWithMode(mode: string): void {
    if (!this.business || !this.cachedMenuData) return;

    this.cartService.setOrderMode(mode as any);

    // Initialize cart for this restaurant
    const currentCart = this.cartService.cart();
    if (!currentCart || currentCart.restaurant_id !== this.business.id) {
      this.cartService.initializeCart(this.business.id, this.business.name, '');
    }

    this.router.navigate(['/restaurant-menu'], {
      state: {
        restaurant: {
          id: this.business.id,
          name: this.business.name,
        },
        menu: this.cachedMenuData,
        source: 'business-detail'
      }
    });
  }

  onReservar(): void {
    if (!this.business) return;

    const today = new Date();
    this.minDate = today.toISOString().split('T')[0];
    this.reservationDate = this.minDate;
    this.selectedSlot = null;
    this.timeSlots = [];
    this.partySize = 2;
    this.reservationNotes = '';
    this.reservationError = '';
    this.selectedServiceId = null;
    this.selectedProfessionalId = null;

    const user = this.authService.getCurrentUser();
    if (user) {
      this.customerName = user.name || '';
    }

    const mode = this.business.booking_mode || 'table';

    // For appointment/service modes, load services first
    if (mode === 'appointment' || mode === 'service') {
      this.reservationStep = 'service';
      this.showReservationModal = true;
      this.loadServicesAndProfessionals();
    } else {
      // table mode — go straight to date
      this.reservationStep = 'date';
      this.showReservationModal = true;
    }
    this.cdr.detectChanges();
  }

  private loadServicesAndProfessionals(): void {
    if (!this.business) return;
    this.loadingServices = true;
    this.cdr.detectChanges();

    this.reservationService.getServices(this.business.id).subscribe({
      next: (services) => {
        this.services = services;
        this.loadingServices = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingServices = false;
        this.services = [];
        this.cdr.detectChanges();
      }
    });

    // Load professionals in parallel
    this.reservationService.getProfessionals(this.business.id).subscribe({
      next: (pros) => { this.professionals = pros; this.cdr.detectChanges(); },
      error: () => { this.professionals = []; }
    });
  }

  selectService(serviceId: number): void {
    this.selectedServiceId = serviceId;
  }

  selectProfessional(professionalId: number | null): void {
    this.selectedProfessionalId = professionalId;
  }

  proceedFromService(): void {
    this.reservationStep = 'date';
    this.cdr.detectChanges();
  }

  get bookingModeLabel(): string {
    switch (this.business?.booking_mode) {
      case 'appointment': return 'Cita';
      case 'service': return 'Servicio';
      case 'table': return 'Mesa';
      default: return 'Reserva';
    }
  }

  closeReservationModal(): void {
    this.showReservationModal = false;
    this.cdr.detectChanges();
  }

  loadAvailability(): void {
    if (!this.business || !this.reservationDate) return;

    this.loadingSlots = true;
    this.selectedSlot = null;
    this.cdr.detectChanges();

    this.reservationService.getAvailability(this.business.id, this.reservationDate).subscribe({
      next: (slots) => {
        this.timeSlots = slots;
        this.loadingSlots = false;
        this.reservationStep = 'time';
        this.cdr.detectChanges();
      },
      error: () => {
        this.reservationError = 'No se pudo cargar la disponibilidad. El servicio de reservas no esta disponible aun.';
        this.loadingSlots = false;
        this.cdr.detectChanges();
      }
    });
  }

  selectTimeSlot(slot: TimeSlot): void {
    if (!slot.available) return;
    this.selectedSlot = slot;
    this.reservationStep = 'details';
    this.cdr.detectChanges();
  }

  goToConfirmReservation(): void {
    this.reservationStep = 'confirm';
    this.cdr.detectChanges();
  }

  submitReservation(): void {
    if (!this.business || !this.selectedSlot || !this.customerName.trim()) return;

    this.submittingReservation = true;
    this.reservationError = '';
    this.cdr.detectChanges();

    const request: ReservationRequest = {
      business_id: this.business.id,
      customer_name: this.customerName,
      customer_phone: this.customerPhone || undefined,
      date: this.reservationDate,
      time_start: this.selectedSlot.slot_start,
      time_end: this.selectedSlot.slot_end,
      party_size: this.partySize,
      professional_id: this.selectedProfessionalId || undefined,
      service_ids: this.selectedServiceId ? [this.selectedServiceId] : undefined,
      notes: this.reservationNotes || undefined
    };

    this.reservationService.createReservation(request).subscribe({
      next: () => {
        this.submittingReservation = false;
        this.reservationStep = 'success';
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.submittingReservation = false;
        this.reservationError = err.error?.message || 'Error al crear la reserva. Intentalo de nuevo.';
        this.cdr.detectChanges();
      }
    });
  }

  reservationStepBack(): void {
    const mode = this.business?.booking_mode || 'table';
    switch (this.reservationStep) {
      case 'date':
        if (mode === 'appointment' || mode === 'service') {
          this.reservationStep = 'service';
        } else {
          this.closeReservationModal();
        }
        break;
      case 'time': this.reservationStep = 'date'; break;
      case 'details': this.reservationStep = 'time'; break;
      case 'confirm': this.reservationStep = 'details'; break;
      default: this.closeReservationModal();
    }
    this.cdr.detectChanges();
  }

  resolveImg(url: string | undefined | null): string {
    return this.configService.resolveImageUrl(url);
  }

  callBusiness(): void {
    if (this.business?.phone) {
      window.location.href = `tel:${this.business.phone}`;
    }
  }

  getDirections(): void {
    if (this.business?.address) {
      const encoded = encodeURIComponent(this.business.address);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
    }
  }

  getStars(rating: number): number[] {
    return Array(Math.floor(rating)).fill(0);
  }

  // ---- Feedback methods ----

  openFeedback(type: 'suggestion' | 'complaint' | 'review'): void {
    this.feedbackType = type;
    this.feedbackTitle = '';
    this.feedbackContent = '';
    this.feedbackRating = 0;
    this.feedbackCategoryFood = 0;
    this.feedbackCategoryService = 0;
    this.feedbackCategoryAmbience = 0;
    this.feedbackCategoryValue = 0;
    this.feedbackSuccess = false;
    this.feedbackError = '';
    this.showFeedbackModal = true;
  }

  closeFeedbackModal(): void {
    this.showFeedbackModal = false;
  }

  setFeedbackRating(value: number): void {
    this.feedbackRating = value;
  }

  get feedbackTypeLabel(): string {
    switch (this.feedbackType) {
      case 'suggestion': return 'Sugerencia';
      case 'complaint': return 'Queja';
      case 'review': return 'Resena';
    }
  }

  get isFeedbackValid(): boolean {
    if (!this.feedbackTitle.trim() || !this.feedbackContent.trim()) return false;
    if (this.feedbackType === 'review' && this.feedbackRating === 0) return false;
    return true;
  }

  submitFeedback(): void {
    if (!this.business || !this.isFeedbackValid) return;

    this.submittingFeedback = true;
    this.feedbackError = '';

    const data: CreateFeedbackRequest = {
      business_id: this.business.id,
      feedback_type: this.feedbackType,
      title: this.feedbackTitle.trim(),
      content: this.feedbackContent.trim(),
      rating: this.feedbackType === 'review' ? this.feedbackRating : undefined,
      category_food: this.feedbackCategoryFood || undefined,
      category_service: this.feedbackCategoryService || undefined,
      category_ambience: this.feedbackCategoryAmbience || undefined,
      category_value: this.feedbackCategoryValue || undefined,
    };

    this.feedbackService.create(data).subscribe({
      next: () => {
        this.submittingFeedback = false;
        this.feedbackSuccess = true;
      },
      error: (err: any) => {
        this.submittingFeedback = false;
        this.feedbackError = err?.error?.error || 'Error al enviar. Intentalo de nuevo.';
      }
    });
  }
}
